import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import actionApi, { Action } from '../../../../../api/action';
import { useAuth } from '../../../../../Auth/Auth';

type Props = {
  isEditing?: boolean;
  selectedRow?: Action;
  setSelectedRow?: React.Dispatch<React.SetStateAction<Action | undefined>>;
  fetchActions?: () => Promise<void>;
};

const DEFAULT_USER_ID = 1;

export const Form: React.FC<Props> = ({
  isEditing = false,
  selectedRow,
  setSelectedRow,
  fetchActions,
}) => {
  const auth = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ok, setOk] = useState<boolean>(false);
  const [action, setAction] = useState<Action>({
    name: '',
    comments: '',
    isActive: 'SI',
    creationId: auth?.user?.id || DEFAULT_USER_ID,
  });

  useEffect(() => {
    if (isEditing && setSelectedRow) {
      setSelectedRow(undefined);
      if (selectedRow) {
        setAction({
          id: selectedRow.id,
          name: selectedRow.name,
          comments: selectedRow.comments,
          isActive: selectedRow.isActive,
          creationId: auth?.user?.id || DEFAULT_USER_ID,
        });
      }
    }
  }, [auth?.user?.id, isEditing, selectedRow, setSelectedRow]);

  const handleOnClickSave = useCallback(async () => {
    setError('');
    const actionId = action.id;
    setIsLoading(true);
    try {
      if (actionId) {
        await actionApi.updateAction(actionId, action);
      } else {
        await actionApi.createAction(action);
      }
      setOk(true);
    } catch {
      setError('Ha ocurrido un error al guardar la acción.');
    }
    setAction({
      name: '',
      comments: '',
      isActive: 'SI',
      creationId: auth?.user?.id || DEFAULT_USER_ID,
    });
    if (isEditing && setSelectedRow) {
      setSelectedRow(undefined);
    }
    setIsLoading(false);
    if (fetchActions) {
      await fetchActions();
    }
  }, [auth?.user?.id, fetchActions, isEditing, action, setSelectedRow]);

  return (
    <>
      <Box
        component='form'
        sx={{
          '& .MuiTextField-root': { m: 1, width: '40ch' },
        }}
        noValidate
        autoComplete='off'
      >
        <TextField
          required
          label='Nombre'
          helperText='Nombre de la Acción'
          value={action.name}
          onChange={(e) =>
            setAction((prevState) => ({ ...prevState, name: e.target.value }))
          }
          error={!!!action.name}
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          label='Observaciones'
          helperText='Observaciones de la Acción'
          value={action.comments ?? ''}
          onChange={(e) =>
            setAction((prevState) => ({
              ...prevState,
              comments: e.target.value,
            }))
          }
        />
        {isEditing && (
          <TextField
            select
            required
            label='Activo'
            value={action.isActive}
            onChange={(e) =>
              setAction((prevState) => ({
                ...prevState,
                isActive: e.target.value,
              }))
            }
          >
            {[
              { value: 'SI', label: 'SI' },
              { value: 'NO', label: 'NO' },
            ].map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      <Button
        variant='contained'
        sx={{ textTransform: 'none' }}
        onClick={handleOnClickSave}
        disabled={
          isLoading ||
          !!!action.name ||
          !!!action.isActive ||
          (isEditing && !!!action.id)
        }
      >
        {isLoading ? <CircularProgress size={25} /> : 'Guardar'}
      </Button>
      {error && <Typography color='red'>{error}</Typography>}
      {ok && (
        <Typography marginTop={'10px'}>
          La acción se ha guardado correctamente.
        </Typography>
      )}
    </>
  );
};
